import { useCallback, useEffect, useRef, useState } from "react";
import { fetchEnterpriseIntakePage } from "../services/enterpriseIntakeAdmin";
import { fetchPreActivationPage } from "../services/preActivationAdmin";

import { LaunchError } from "../utils/errors";
import { store } from "../app/store";

const getSafeCreationTimeNegative = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildMergedRows = (preRows, entRows) => {
  const fromPre = preRows.map((r) => ({
    formId: r.formId,
    submissionType: "preActivation",
    brandName: r.businessName,
    fullName: r.fullName,
    email: r.email,
    whatsApp: r.whatsApp,
    organizationType: r.organizationType,
    creationTime: r.creationTime,
    creationTimeNegative: r.creationTimeNegative,
  }));
  const fromEnt = entRows.map((r) => ({
    formId: r.formId,
    submissionType: "enterpriseIntake",
    brandName: r.companyName,
    fullName: r.fullName,
    email: r.corporateEmail,
    whatsApp: "",
    organizationType: r.organizationType,
    creationTime: r.creationTime,
    creationTimeNegative: r.creationTimeNegative,
  }));
  return [...fromPre, ...fromEnt].sort((a, b) => {
    const aTime = getSafeCreationTimeNegative(a.creationTimeNegative);
    const bTime = getSafeCreationTimeNegative(b.creationTimeNegative);

    if (aTime === null && bTime === null) {
      return a.formId.localeCompare(b.formId);
    }
    if (aTime === null) return 1;
    if (bTime === null) return -1;

    if (aTime !== bTime) return aTime - bTime;

    return a.formId.localeCompare(b.formId);
  });
};

const useOwnerFormSubmissions = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  const preCursorRef = useRef(null);
  const entCursorRef = useRef(null);
  const preHasMoreRef = useRef(true);
  const entHasMoreRef = useRef(true);
  const preAccumRef = useRef([]);
  const entAccumRef = useRef([]);

  const recomputeRows = () =>
    buildMergedRows(preAccumRef.current, entAccumRef.current);

  useEffect(() => {
    let cancelled = false;

    const loadInitial = async () => {
      setLoading(true);
      setError(null);
      preCursorRef.current = null;
      entCursorRef.current = null;
      preHasMoreRef.current = true;
      entHasMoreRef.current = true;
      preAccumRef.current = [];
      entAccumRef.current = [];

      try {
        const [preRes, entRes] = await Promise.all([
          fetchPreActivationPage(null),
          fetchEnterpriseIntakePage(null),
        ]);
        if (cancelled) return;

        preAccumRef.current = preRes.rows;
        entAccumRef.current = entRes.rows;
        preCursorRef.current = preRes.cursorAfter;
        entCursorRef.current = entRes.cursorAfter;
        preHasMoreRef.current = preRes.hasMore;
        entHasMoreRef.current = entRes.hasMore;

        setRows(recomputeRows());
        setHasMore(preRes.hasMore || entRes.hasMore);
      } catch (e) {
        store.dispatch(
          LaunchError(e, false, [], {
            functionName: "loadInitial",
            capture: "exception",
          })
        );
        if (!cancelled) {
          setError(e);
          setRows([]);
          setHasMore(false);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadInitial();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || loading) return;
    if (!preHasMoreRef.current && !entHasMoreRef.current) return;

    setLoadingMore(true);
    setError(null);
    try {
      const prePromise = preHasMoreRef.current
        ? fetchPreActivationPage(preCursorRef.current)
        : Promise.resolve({
            rows: [],
            cursorAfter: preCursorRef.current,
            hasMore: false,
          });
      const entPromise = entHasMoreRef.current
        ? fetchEnterpriseIntakePage(entCursorRef.current)
        : Promise.resolve({
            rows: [],
            cursorAfter: entCursorRef.current,
            hasMore: false,
          });
      const [preRes, entRes] = await Promise.all([prePromise, entPromise]);

      preAccumRef.current = [...preAccumRef.current, ...preRes.rows];
      entAccumRef.current = [...entAccumRef.current, ...entRes.rows];
      preCursorRef.current = preRes.cursorAfter;
      entCursorRef.current = entRes.cursorAfter;
      preHasMoreRef.current = preRes.hasMore;
      entHasMoreRef.current = entRes.hasMore;

      setRows(recomputeRows());
      setHasMore(preHasMoreRef.current || entHasMoreRef.current);
    } catch (e) {
      store.dispatch(
        LaunchError(e, false, [], {
          functionName: "loadInitial",
          capture: "exception",
        })
      );
      setError(e);
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, loading]);

  return {
    rows,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
  };
};

export default useOwnerFormSubmissions;
